/**
 * Form Validation Helper Utilities
 * Shared validation utilities for multi-part form fields (phone, fax, postal code, etc.)
 */

/**
 * Initialize conditional validation for multi-part fields
 * @param {jQuery} container - Form container element
 * @param {Array<string>} fieldNames - Array of field names to validate together
 * @param {Object} options - Optional configuration
 * @param {boolean} options.preventInputValidation - Prevent validation on input events (default: true)
 * @param {number} options.validationDelay - Delay before validation in ms (default: 10)
 */
export const initConditionalValidation = (container, fieldNames, options = {}) => {
    const { preventInputValidation = true, validationDelay = 10 } = options;

    const fields = container.find(fieldNames.map(name => `[name="${name}"]`).join(', '));

    // Only trigger validation on blur to prevent flickering
    const eventType = preventInputValidation ? 'blur' : 'input blur';

    fields.on(eventType, () => {
        // Trigger validation for all related fields
        fields.each(function () {
            $(this).trigger('change');
        });

        // Force validation after a short delay to ensure DOM updates
        setTimeout(() => {
            const form = container.closest('form');
            if (!form.length || !form.data('validator')) {
                return;
            }

            const validator = form.data('validator');

            // Get all field values
            const values = fieldNames.map(name => form.find(`[name="${name}"]`).val()?.trim() || '');

            // Check if any field has a value
            const hasAnyValue = values.some(val => val !== '');

            if (hasAnyValue) {
                // If any field has value, ensure all have values
                const allFieldsHaveValues = values.every(val => val !== '');

                if (!allFieldsHaveValues) {
                    // Show error for empty fields
                    fields.each(function () {
                        const fieldValue = $(this).val()?.trim() || '';
                        if (fieldValue === '') {
                            validator.element(this);
                        }
                    });
                }
            }
        }, validationDelay);
    });
};

/**
 * Initialize numeric input restriction for phone/fax/postal code fields
 * @param {jQuery} container - Form container element
 * @param {Array<string>} fieldSelectors - Array of field selectors to restrict
 */
export const initNumericInputRestriction = (container, fieldSelectors) => {
    const selector = fieldSelectors.map(sel => `#${sel}`).join(', ');

    container.find(selector).on('input', e => {
        e.target.value = e.target.value.replace(/\D/g, '');
    });
};

/**
 * Initialize postal code auto-formatting
 * @param {jQuery} container - Form container element
 * @param {string} field1Id - First postal code field ID
 * @param {string} field2Id - Second postal code field ID
 * @param {number} maxLength1 - Max length for first field (default: 3)
 * @param {number} maxLength2 - Max length for second field (default: 4)
 */
export const initPostalCodeFormatting = (container, field1Id, field2Id, maxLength1 = 3, maxLength2 = 4) => {
    container.find(`#${field1Id}`).on('input', e => {
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length > maxLength1) {
            value = value.substring(0, maxLength1);
        }
        e.target.value = value;
    });

    container.find(`#${field2Id}`).on('input', e => {
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length > maxLength2) {
            value = value.substring(0, maxLength2);
        }
        e.target.value = value;
    });
};

/**
 * Initialize Enter key prevention for form elements
 * @param {jQuery} container - Form container element
 */
export const initEnterKeyPrevention = container => {
    container.on('keydown', 'input, textarea, select', e => {
        if (e.key === 'Enter') {
            // Allow Enter key in textarea to create new lines
            if (e.target.tagName === 'TEXTAREA') {
                return true;
            }
            // Prevent Enter key for input and select elements
            e.preventDefault();
            e.stopPropagation();
            return false;
        }
    });
};

/**
 * Initialize postal code autofill functionality
 * @param {jQuery} container - Form container element
 * @param {Function} autofillAddressFunc - Address autofill function
 * @param {string} field1Id - First postal code field ID
 * @param {string} field2Id - Second postal code field ID
 * @param {string} addressFieldId - Address field ID
 * @param {string} prefectureFieldId - Prefecture field ID
 */
export const initPostalCodeAutofill = (container, autofillAddressFunc, field1Id, field2Id, addressFieldId, prefectureFieldId) => {
    container.find(`#${field1Id}, #${field2Id}`).on('blur', () => {
        const zipCode1 = container.find(`#${field1Id}`).val();
        const zipCode2 = container.find(`#${field2Id}`).val();

        if (zipCode1 && zipCode2) {
            const addressData = autofillAddressFunc(zipCode1, zipCode2);
            if (addressData) {
                // Set values first
                container.find(`#${addressFieldId}`).val(addressData.address);
                container.find(`#${prefectureFieldId}`).val(addressData.prefecture);

                // Clear error classes and messages manually
                container.find(`#${prefectureFieldId}`).removeClass('error border-red-500 focus:ring-red-500 focus:border-red-500');
                container.find(`#${addressFieldId}`).removeClass('error border-red-500 focus:ring-red-500 focus:border-red-500');
                container.find(`#${prefectureFieldId}Error`).addClass('hidden').text('');
                container.find(`#${addressFieldId}Error`).addClass('hidden').text('');

                // Trigger validation to re-validate fields
                container.find(`#${prefectureFieldId}`).trigger('change');
                container.find(`#${addressFieldId}`).trigger('change');
            }
        }
    });
};

/**
 * Split full postal code into parts
 * @param {string} fullPostalCode - Full postal code (e.g., "123-4567")
 * @returns {Array} Array of postal code parts [part1, part2]
 */
export const splitPostalCode = fullPostalCode => {
    return fullPostalCode ? fullPostalCode.split('-') : ['', ''];
};

/**
 * Split full phone number into parts
 * @param {string} fullPhoneNumber - Full phone number (e.g., "03-1234-5678")
 * @returns {Array} Array of phone number parts [part1, part2, part3]
 */
export const splitPhoneNumber = fullPhoneNumber => {
    return fullPhoneNumber ? fullPhoneNumber.split('-') : ['', '', ''];
};

/**
 * Combine postal code parts into full code
 * @param {string} part1 - First part (3 digits)
 * @param {string} part2 - Second part (4 digits)
 * @returns {string} Combined postal code
 */
export const combinePostalCode = (part1, part2) => {
    return part1 && part2 ? `${part1}-${part2}` : '';
};

/**
 * Combine phone/fax parts into full number
 * @param {string} part1 - First part
 * @param {string} part2 - Second part
 * @param {string} part3 - Third part
 * @returns {string} Combined phone/fax number
 */
export const combinePhoneNumber = (part1, part2, part3) => {
    return [part1, part2, part3]
        .filter(p => p && p.trim())
        .map(p => p.trim())
        .join('-');
};

/**
 * Clean form data by removing empty strings and replacing with null
 * @param {Object} formData - Raw form data
 * @returns {Object} Cleaned form data
 */
export const cleanFormData = formData => {
    const cleanData = { ...formData };

    // Remove empty strings and replace with null
    Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === '') {
            cleanData[key] = null;
        }
    });

    return cleanData;
};
